import { SubscriptionStatsDetail } from '@type/v1-api-types';
import { StatisticsCard } from './StatisticsCard';


// Interface
// *****************************************************
export interface SubscriptionStatsDetailCard {
  title?: string,
  statsCategory?: SubscriptionStatsDetail,
  className?: string,
  shouldRender?: boolean,
  ctaTitle?: string,
  ctaLink?: string,
  hideUsedColumn?: boolean,
  hideConsumptionMeter?: boolean,
  hideTotalColumn?: boolean,
  type?: 'job' | 'contact' | 'cv',
}

interface StatisticsListProps {
  stats?: SubscriptionStatsDetailCard[],
  className?: string,
  showConsumptionMeter?: boolean,
}


/**
 * @description Statistics list component. It shows a list of statistics cards. It will not render
 * if there are no stats. It will not render a statistics card if shouldRender is false.
 * * **stats** - the statistics data. Default: undefined.
 * * **className** - additional classes for the component. Default: ''.
 *
 * @example
 * // create a list of statistics cards
 * const stats = [
 *  {
 *    title: 'Joburi incluse',
 *    statsCategory: stats.includedJobs,
 *    shouldRender: stats.includedJobs?.total > 0,
 *  },
 *  {
 *    title: 'Joburi publicate',
 *    statsCategory: stats.publishedJobs,
 *    className: 'col-span-2',
 *    shouldRender: true,
 *  },
 *  {
 *    title: 'Joburi vizualizate',
 *    statsCategory: stats.viewedJobs,
 *    shouldRender: true,
 *  },
 * ];
 *
 * // display the statistics list
 * <StatisticsList stats={stats} />
 */
export const StatisticsList = (props: StatisticsListProps) => {
  // Destructure props
  const {
    stats,
    className = '',
    showConsumptionMeter = true,
  } = props;


  // Return null if there are no stats
  // *****************************************************
  if (!stats || stats.length === 0) return null;

  // Render component
  // We have a dynamic number of statistics cards, use 'grid-flow-col auto-cols-fr' to make them
  // *****************************************************
  return (
    <div className={`my-5 grid gap-3 sm:grid-cols-2 sm:gap-5 lg:my-9 lg:auto-cols-fr lg:grid-flow-col ${className}`}>
      {
        stats.map((stat) => {
          // Return null if shouldRender is false
          if (!stat?.shouldRender) return null;
          // Render the statistics card
          return (
            <StatisticsCard
              key={stat?.title}
              title={stat?.title}
              hideUsedColumn={stat?.hideUsedColumn}
              hideTotalColumn={stat?.hideTotalColumn}
              statsCategory={stat?.statsCategory}
              className={stat?.className}
              ctaTitle={stat?.ctaTitle}
              ctaLink={stat?.ctaLink}
              type={stat?.type}
              showConsumptionMeter={showConsumptionMeter && !stat?.hideConsumptionMeter}
            />
          );
        })
      }
    </div>
  );
};
